import { useEffect, useRef, useState } from 'react';

/**
 * 设备框自适应缩放。
 *
 * 直接移植原型 app.js 的 fit()：桌面端把 390×844 的手机框等比缩放到可视区内，
 * 并用负外边距补偿缩放后的布局占位；窄屏（≤520px）完全放弃缩放，走全屏形态。
 *
 * 保留这段逻辑的原因：验收与汇报场景都在桌面浏览器上进行，评审者需要看到
 * 「手机形态」；而真实用户用手机打开时它就是全屏应用，不受影响。
 */
export function useDeviceScale() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fit = (): void => {
      const device = ref.current;
      if (!device) return;
      if (window.innerWidth <= 520) {
        device.style.transform = '';
        device.style.margin = '';
        return;
      }
      const availH = window.innerHeight - 90;
      // 桌面端两侧留呼吸位；宽屏时多留一些，避免手机框贴边
      const availW = window.innerWidth - (window.innerWidth > 1080 ? 160 : 80);
      const scale = Math.min(1, availH / 844, Math.max(availW, 320) / 390);
      device.style.transform = `scale(${scale.toFixed(3)})`;
      device.style.margin = `${(((scale - 1) * 844) / 2).toFixed(0)}px ${(((scale - 1) * 390) / 2).toFixed(0)}px`;
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  return ref;
}

/** 设备框内状态栏时钟：显示真实时间（原型那个状态栏是纯装饰的假壳）。 */
export function useClock(): string {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

/** 首屏滚动到顶部：SPA 切路由时若不复位，用户会停在上一屏的滚动位置。 */
export function useScrollReset(deps: unknown[], container: HTMLElement | null): void {
  useEffect(() => {
    if (container) container.scrollTop = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
